import {Suspense, useState} from 'react'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {Blankslate} from '@primer/react/experimental'
import {Button, RelativeTime} from '@primer/react'
import {CheckIcon, ChevronDownIcon, ChevronUpIcon} from '@primer/octicons-react'
import {CollapsiblePanel} from './CollapsiblePanel'
import {ExpandableContent} from './ExpandableContent'
import {useCatchUpQuery} from './data/route-queries'
import {useRouteQuery} from '@github-ui/react-core/future/use-route-query'
import {pullRequestsLayoutRoute} from '../../routes/layout-route'
import type {CatchUpPayload} from '../../routes/route-payload-types'
import {buildEventGroups} from './catch-up-helpers'
import {EventList} from './CatchUpEventList'
import styles from './CatchUp.module.css'
import type {NavigationUrls} from '../../types/navigation-urls-types'
import {Link} from '@github-ui/react-core/link'

/** Catch Up section showing recent timeline updates since last engagement */
export function CatchUp() {
  return (
    <CollapsiblePanel heading="Catch up">
      <ErrorBoundary boundaryName="CatchUp" fallback={null}>
        <Suspense fallback={null}>
          <CatchUpContent />
        </Suspense>
      </ErrorBoundary>
    </CollapsiblePanel>
  )
}

function CatchUpContent() {
  const {data: catchUpData} = useCatchUpQuery()
  const {data: layoutData} = useRouteQuery(pullRequestsLayoutRoute, 'mainQuery')

  return <CatchUpComponent catchUpData={catchUpData} urls={layoutData.urls} />
}

function CatchUpComponent({catchUpData, urls}: {catchUpData: CatchUpPayload; urls: NavigationUrls}) {
  const {changes: changesUrl, conversation: conversationUrl} = urls
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(() => new Set())

  // Show empty state if there's no timestamp or no new items
  if (!catchUpData.sinceTimestamp || catchUpData.summary.total === 0) {
    return (
      <ExpandableContent sectionLabel="Catch up">
        <Blankslate>
          <Blankslate.Visual>
            <CheckIcon size={24} />
          </Blankslate.Visual>
          <Blankslate.Heading>You&apos;re all caught up</Blankslate.Heading>
          <Blankslate.Description>
            {!catchUpData.sinceTimestamp
              ? "We'll show new activity here once you interact with this pull request."
              : 'No new activity since your last visit.'}
          </Blankslate.Description>
        </Blankslate>
      </ExpandableContent>
    )
  }

  const summary = catchUpData.summary
  const items = buildEventGroups(summary, catchUpData.sinceTimestamp, changesUrl)

  const toggleGroup = (type: string) => {
    setExpandedGroups(prev => {
      const next = new Set(prev)
      if (next.has(type)) {
        next.delete(type)
      } else {
        next.add(type)
      }
      return next
    })
  }

  return (
    <ExpandableContent sectionLabel="Catch up" maxHeight={1000}>
      <div className={styles.container}>
        <p className={styles.timestamp}>
          Since <RelativeTime date={new Date(catchUpData.sinceTimestamp)} />
        </p>
        <ul className={styles.list}>
          {items.map(item => {
            const isExpanded = expandedGroups.has(item.type)
            return (
              <li key={`catch-up-item-${item.type}`} className={styles.item}>
                <div className={styles.itemHeader}>
                  <span className={styles.label}>{item.label}</span>
                  <div className={styles.itemActions}>
                    {item.link && (
                      <Button
                        as={Link}
                        to={item.link}
                        size="small"
                        variant="invisible"
                        aria-label={`View ${item.label.toLowerCase()} in Files tab`}
                      >
                        View changes
                      </Button>
                    )}
                    {item.count > 0 && (
                      <Button
                        aria-expanded={isExpanded}
                        aria-label={`${isExpanded ? 'Hide' : 'Show'} details for ${item.label.toLocaleLowerCase()}`}
                        size="small"
                        variant="invisible"
                        onClick={() => toggleGroup(item.type)}
                        trailingVisual={isExpanded ? ChevronUpIcon : ChevronDownIcon}
                      />
                    )}
                  </div>
                </div>
                {isExpanded && (
                  <EventList
                    events={item.events}
                    type={item.type}
                    changesUrl={changesUrl}
                    conversationUrl={conversationUrl}
                  />
                )}
              </li>
            )
          })}
        </ul>
      </div>
    </ExpandableContent>
  )
}
